// The Cover Letter is the leader's brief that sits in front of a Part 2 lesson.
// It is not one of the lesson's pages — it carries no page number, is absent from
// `totalPages` and takes no part in progress — but it IS a place the reader can be
// left standing on, so "where I left off" has to be able to name it. Page 0 is that
// name: the pages behind the brief are 1..totalPages, so 0 is free and sorts before
// all of them.
//
// Consequence worth remembering: 0 is falsy. Every test for "is a page remembered"
// must ask whether the value is a NUMBER, never whether it is truthy — `if (page)`
// silently reads the cover as "nothing remembered". See setLessonPage in the
// courses store and ionViewWillEnter in pages/courses/_lessonId.vue.
export const COVER_LETTER_PAGE = 0;

// The IDR unlock-card lane: the scanned-card screen (`/courses/redeem/:code`), the
// redemption it calls, and the typed-code entry on My Vouchers that reaches the
// same API without a camera. Off in production until the printed cards are out and
// IDR has confirmed the contentKey the run was cut with.
//
// Shared rather than declared per consumer: a member-facing trigger that outlives
// its own lane is a dead end — the wallet's Redeem button would open a sheet whose
// submit the server answers with a 404 while the feature is dormant. One constant
// is what keeps the door and the room on the same switch.
export const IDR_UNLOCK_FLAG = 'idr-unlock-card';

// The Leader's Dashboard unlock QR: the dashboard's QR icon, the consent screen
// and the code itself (`/courses/:programSlug/leader/qr[/code]`), the mint call
// behind them, and — API-side — redemption of an outstanding code.
//
// SEPARATE from `leader-dashboard`, which is already ON in production. This flag
// existed, was retired on 2026-09-01 while the feature was development-only, and is
// back for the production cut: a QR that grants a member FULL access to a paid
// program for free cannot ship gated only by a switch that is already thrown. Off
// means the icon is hidden, the routes fall back to Home, and the API 404s both
// minting and redemption — so a member who somehow holds a printed code gets
// nothing rather than a screen that renders and then fails.
//
// The string is the contract with hogc-one-api (LEADER_DASHBOARD_QR_FLAG) and with
// the `feature_flag` row of the same name. All three must agree; a mismatch fails
// silently, and not in the safe direction on every side.
export const LEADER_DASHBOARD_QR_FLAG = 'leader-dashboard-qr';

// The Zone tab's OWN switch, on top of `leader-dashboard`.
//
// The dashboard is two features on one screen: My CG (a leader marking lessons
// for their own members) and Zone (a leaderboard of every CG in their zone(s),
// with a per-CG detail behind each row). Until 2026-09-03 both rode
// `leader-dashboard`, so a leader got neither or both. This flag lets a leader be
// given the marking function alone, and lets the zone rollup — the wide-scope
// read behind the 2026-09-02 incident — be switched off or targeted without
// taking marking down with it.
//
// Off means the My CG / Zone tab bar is not rendered and the dashboard opens
// straight onto the roster; the API 404s `/leader/zones` and `/leader/cgs/:id`
// on the same flag. Adds to `leader-dashboard`, never replaces it: the route
// guard still requires the dashboard's flag to get here at all.
//
// The string is the contract with hogc-one-api (LEADER_DASHBOARD_ZONE_FLAG) and
// the `feature_flag` row of the same name. A mismatch fails silently.
export const LEADER_DASHBOARD_ZONE_FLAG = 'leader-dashboard-zone';

// The test-only switch that puts the Next Steps notes features back on web.
//
// Encrypted notes are a native feature (encryption spec, decision 7 / section
// 6.10): the member key lives in the iOS Keychain and in Android backup, and a
// web build has nothing safe to hold a key in. Production web therefore offers
// no highlights, notes, tags, Notes Hub or blank inputs — lessons stay readable.
// The exception is the courses Playwright suite, which exercises all of it in a
// browser; this flag is what keeps that coverage. Seeded off everywhere by
// hogc-one-api PR #1083 and switched on by hand in development and staging
// only, never in production.
//
// SHARED because two lanes read it from opposite ends of the same decision:
// `utils/courses-notes-availability` answers "may this build offer the notes
// features", and `utils/courses-crypto/platform-key-adapter` answers "may the
// web adapter hold a key at all". They must never disagree, and a string
// duplicated per consumer is exactly how they would.
export const COURSES_NOTES_WEB_FLAG = 'courses-notes-web';

// The flag that gates ENROLMENT in end-to-end encrypted Next Steps notes, and
// nothing else (encryption spec section 6.9): the same name on both halves,
// off in production. It never gates decryption, device registration, approval
// or recovery — a member who has enrolled must keep working the moment it is
// switched off, so those paths are live once shipped and only the silent
// enrolment in `utils/courses-encryption/enrolment.js` and the API's
// `POST /api/me/encryption` read this.
export const COURSES_E2EE_FLAG = 'courses-e2ee';

/**
 * ⚠️ The flag that gates ROTATION — "Sign out all other devices" (#1694) — and
 * nothing else. Off everywhere until the defects below are fixed.
 *
 * WHY THIS EXISTS SEPARATELY FROM `courses-e2ee`. That flag gates enrolment and
 * deliberately gates nothing else, so switching it off protects nobody who has
 * already enrolled — and rotation is reachable by every enrolled member the
 * moment they open the Devices screen. It needed a gate of its own and did not
 * get one when it shipped, which is the process miss underneath the rest.
 *
 * WHAT A MEMBER RISKS WITHOUT IT. An adversarial review on 2026-09-09 found
 * three ways to leave an account permanently unable to write, each ending with
 * "Start over" — erasing every note — as the only exit:
 *
 *   - a rotation that can never commit, because the walk skipped a whole row
 *     type (fixed in #1727);
 *   - a lost response from `rotate/begin`, after which the client discards the
 *     only copy of the new key while the server keeps the rotation open;
 *   - signing out, or losing the phone, while a rotation is open — the sign-out
 *     wipe takes the pending generation with it and nothing else holds it.
 *
 * `rotate/abort` has no caller, so none of those states can be backed out of.
 *
 * REMOVE THIS FLAG once those are fixed and rotation has run end to end on a
 * member with all four row types. A permanent flag is a permanent branch.
 */
export const COURSES_E2EE_ROTATION_FLAG = 'courses-e2ee-rotation';

// How long a `/courses/access` summary counts as "just fetched" — long enough to
// span one navigation (the pastoral guard resolves in `beforeEnter`, the lesson
// gate asks again ~100 ms later on enter), far too short for anything a member
// could notice as stale. Only callers on the same navigation as a fetch pass it;
// the guard and every grant path fetch fresh. See fetchCourseAccess (#1812).
export const ACCESS_FRESH_MS = 3000;
